import React, { useEffect } from 'react';
import { Building2, DollarSign, CheckCircle, Factory, RefreshCw } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { GlassCard } from '../components/ui/GlassCard';

export const PermanentInstall = ({ onOpenQuote }: { onOpenQuote: () => void }) => {
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen pt-20">
      
      {/* Hero Section */}
      <section className="relative py-24 px-4 overflow-hidden flex items-center min-h-[85vh]">
        <div className="absolute inset-0 z-0">
           <img 
            src="https://images.unsplash.com/photo-1576267423048-15c0040fec78?auto=format&fit=crop&q=80&w=2000" 
            className="w-full h-full object-cover opacity-30 grayscale"
            alt="Venue Interior"
           />
           <div className="absolute inset-0 bg-gradient-to-b from-slate-950/80 via-slate-950/90 to-slate-950"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto w-full text-center">
            <div className="inline-flex items-center gap-2 mb-6 px-4 py-2 rounded-full bg-white/5 border border-white/20 text-white text-xs font-bold uppercase tracking-wider animate-fade-in-up">
              <Factory size={14} className="text-brand-neonPink" /> NZ Made Hardware
            </div>
            <h1 className="text-5xl md:text-7xl font-display font-bold text-white mb-6 leading-tight animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
              Permanent Fleets & <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand-neonBlue to-brand-neonPink">Venue Infrastructure.</span>
            </h1>
            <p className="text-xl text-slate-300 mb-10 leading-relaxed max-w-3xl mx-auto animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
              We design, manufacture, and operate premium photo hardware for New Zealand's busiest venues. From single-unit venue installs to multi-site convention fleets.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in-up" style={{ animationDelay: '0.3s' }}>
              <Button size="lg" onClick={onOpenQuote}>Request Hardware Catalog</Button>
              <Button variant="outline" size="lg">View Leasing Models</Button>
            </div>
        </div>
      </section>

      {/* Manufacturing Showcase */}
      <section className="py-24 bg-slate-900 border-y border-white/5 relative">
        <div className="max-w-7xl mx-auto px-4">
           <div className="flex flex-col md:flex-row justify-between items-end mb-16">
              <div>
                <span className="text-brand-neonBlue font-bold tracking-widest text-xs uppercase mb-2 block">Direct from Manufacturer</span>
                <h2 className="text-3xl md:text-5xl font-display font-bold text-white">We Build What We Rent.</h2>
                <p className="text-slate-400 mt-4 max-w-xl">
                  Unlike competitors who import plastic shells, we fabricate industrial-grade units here in NZ. 
                  Designed for 24/7 uptime and heavy crowd abuse.
                </p>
              </div>
              <Button variant="outline" className="mt-6 md:mt-0">Book a Showroom Visit</Button>
           </div>

           <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {/* Product 1: Kiosk */}
              <div className="group">
                <div className="h-80 bg-slate-800 rounded-2xl mb-6 relative overflow-hidden border border-white/5 group-hover:border-brand-neonBlue/50 transition-all">
                  <img src="https://images.unsplash.com/photo-1595769816263-9b910be24d5f?auto=format&fit=crop&q=80&w=800" className="w-full h-full object-cover opacity-70 group-hover:scale-105 transition-transform duration-500" alt="Kiosk" />
                  <div className="absolute top-4 left-4 bg-black/60 backdrop-blur text-white text-xs font-bold px-2 py-1 rounded">The Halo Kiosk</div>
                </div>
                <h3 className="text-xl font-bold text-white mb-2">Freestanding Kiosk</h3>
                <p className="text-sm text-slate-400">
                  Rugged steel enclosure with integrated RGB lighting. Customizable vinyl wrap. Perfect for lobbies and foyers.
                </p>
              </div>

              {/* Product 2: Wall / Bar Mount */}
              <div className="group">
                <div className="h-80 bg-slate-800 rounded-2xl mb-6 relative overflow-hidden border border-white/5 group-hover:border-brand-neonPink/50 transition-all">
                  <img src="https://images.unsplash.com/photo-1542204625-ca960ca44635?auto=format&fit=crop&q=80&w=800" className="w-full h-full object-cover opacity-70 group-hover:scale-105 transition-transform duration-500" alt="Wall Mount" />
                  <div className="absolute top-4 left-4 bg-black/60 backdrop-blur text-white text-xs font-bold px-2 py-1 rounded">The Slimline</div>
                </div>
                <h3 className="text-xl font-bold text-white mb-2">Wall & Bar Mounts</h3>
                <p className="text-sm text-slate-400">
                  Zero-footprint units that bolt onto walls or clamp onto bar leaners. VESA compatible.
                </p>
              </div>

              {/* Product 3: Roaming Handheld */}
              <div className="group">
                <div className="h-80 bg-slate-800 rounded-2xl mb-6 relative overflow-hidden border border-white/5 group-hover:border-yellow-400/50 transition-all">
                  <img src="https://images.unsplash.com/photo-1522202176988-66273c2fd55f?auto=format&fit=crop&q=80&w=800" className="w-full h-full object-cover opacity-70 group-hover:scale-105 transition-transform duration-500" alt="Roaming" />
                  <div className="absolute top-4 left-4 bg-black/60 backdrop-blur text-white text-xs font-bold px-2 py-1 rounded">The Roamer</div>
                </div>
                <h3 className="text-xl font-bold text-white mb-2">Handheld Roamers</h3>
                <p className="text-sm text-slate-400">
                  Lightweight, battery-powered ring light units for staff to circulate the dance floor. 
                </p>
              </div>
           </div>
        </div>
      </section>

      {/* Target Segments / Business Models */}
      <section className="py-24 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4">
           <h2 className="text-3xl md:text-5xl font-display font-bold text-white mb-16 text-center">Business Models</h2>
           
           <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
             
             {/* Model 1: Corporate Fleet */}
             <GlassCard className="p-8 border-brand-neonBlue/30 bg-slate-900/50">
                <div className="w-12 h-12 rounded-lg bg-brand-neonBlue/10 flex items-center justify-center text-brand-neonBlue mb-6">
                  <Building2 size={24} />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">Enterprise Fleet</h3>
                <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-6">For Convention Centres & Casinos</div>
                
                <ul className="space-y-4 mb-8">
                  <li className="flex items-start gap-3">
                     <CheckCircle size={16} className="text-brand-neonBlue shrink-0 mt-1" />
                     <p className="text-slate-300 text-sm">Hardware on long-term loan.</p>
                  </li>
                  <li className="flex items-start gap-3">
                     <CheckCircle size={16} className="text-brand-neonBlue shrink-0 mt-1" />
                     <p className="text-slate-300 text-sm">Pay-per-event usage + Monthly Minimum.</p>
                  </li>
                  <li className="flex items-start gap-3">
                     <CheckCircle size={16} className="text-brand-neonBlue shrink-0 mt-1" />
                     <p className="text-slate-300 text-sm">Includes 24/7 priority support & rapid replacement.</p>
                  </li>
                </ul>
                <Button className="w-full" onClick={onOpenQuote}>Request Rate Card</Button>
             </GlassCard>

             {/* Model 2: Venue Partner */}
             <GlassCard className="p-8 border-brand-neonPink/30 bg-slate-900/50 relative overflow-hidden">
                <div className="absolute top-0 right-0 bg-brand-neonPink text-white text-[10px] font-bold px-3 py-1 rounded-bl-lg">HIGH DEMAND</div>
                <div className="w-12 h-12 rounded-lg bg-brand-neonPink/10 flex items-center justify-center text-brand-neonPink mb-6">
                  <RefreshCw size={24} />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">Venue Sub-Hire</h3>
                <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-6">For Wedding Venues</div>
                
                <ul className="space-y-4 mb-8">
                  <li className="flex items-start gap-3">
                     <CheckCircle size={16} className="text-brand-neonPink shrink-0 mt-1" />
                     <p className="text-slate-300 text-sm">We install a permanent booth in your foyer.</p>
                  </li>
                  <li className="flex items-start gap-3">
                     <CheckCircle size={16} className="text-brand-neonPink shrink-0 mt-1" />
                     <p className="text-slate-300 text-sm">You sub-hire it to couples as an "Add-on".</p>
                  </li>
                  <li className="flex items-start gap-3">
                     <CheckCircle size={16} className="text-brand-neonPink shrink-0 mt-1" />
                     <p className="text-slate-300 text-sm">Instant profit with zero logistics work for you.</p>
                  </li>
                </ul>
                <Button variant="secondary" className="w-full" onClick={onOpenQuote}>Become a Partner</Button>
             </GlassCard>

             {/* Model 3: Hospitality Revenue */}
             <GlassCard className="p-8 border-white/10 bg-slate-900/50">
                <div className="w-12 h-12 rounded-lg bg-white/10 flex items-center justify-center text-white mb-6">
                  <DollarSign size={24} />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">Revenue Share</h3>
                <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-6">For Bars & Clubs</div>
                
                <ul className="space-y-4 mb-8">
                  <li className="flex items-start gap-3">
                     <CheckCircle size={16} className="text-white shrink-0 mt-1" />
                     <p className="text-slate-300 text-sm">Turn dead wall space into cash.</p>
                  </li>
                  <li className="flex items-start gap-3">
                     <CheckCircle size={16} className="text-white shrink-0 mt-1" />
                     <p className="text-slate-300 text-sm">Guests pay per photo (Tap to Pay).</p>
                  </li>
                  <li className="flex items-start gap-3">
                     <CheckCircle size={16} className="text-white shrink-0 mt-1" />
                     <p className="text-slate-300 text-sm">We handle maintenance, you cash the check.</p>
                  </li>
                </ul>
                <Button variant="outline" className="w-full" onClick={onOpenQuote}>Get Revenue Estimate</Button>
             </GlassCard>

           </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-slate-950 border-t border-white/5">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-4xl font-display font-bold text-white mb-6">Got a custom build in mind?</h2>
          <p className="text-lg text-slate-300 mb-8">
            Because we manufacture in-house, we can build custom housings to match your venue's architecture. 
            Wood, brass, or powder-coated steel to match your brand colors.
          </p>
          <Button size="lg" variant="secondary" onClick={onOpenQuote}>
            Contact Manufacturing Team
          </Button>
        </div>
      </section>

    </div>
  );
};
